/**
 * Environment identification for the UI (CAO-212).
 *
 * Staging runs with production cloud credentials and can launch real VMs, so a
 * staging tab must never be mistakable for a production one. The marker is driven
 * by an explicit build-time variable rather than NODE_ENV, because staging is built
 * with `npm run build` (NODE_ENV === 'production') and would otherwise look exactly
 * like production.
 *
 *   REACT_APP_ENV_LABEL=DEV | STAGING | ... — shown as a chip next to the wordmark
 *   unset / empty                          — production: no chip, nothing changes
 *
 *   Dev:    unset is fine — NODE_ENV === 'development' falls back to DEV, so local
 *           development keeps its chip with zero configuration.
 *   Deploy: frontend/Dockerfile.staging defaults the build arg to STAGING and
 *           scripts/cicd/staging.sh passes it through. The production frontend
 *           build deliberately does not set it.
 *
 * CRA only inlines env vars prefixed with REACT_APP_, and it does so at compile
 * time by substituting the literal `process.env.REACT_APP_ENV_LABEL` expression —
 * which is why the read below is a literal member access and not a lookup on a
 * `process.env` object passed around. Changing the value needs a rebuild, not a
 * redeploy of an existing image.
 */

/** MUI palette slot used to colour the chip. */
export type EnvLabelColor = 'warning' | 'secondary' | 'info';

export interface EnvLabel {
  /** Uppercased label text, e.g. 'DEV' or 'STAGING'. */
  label: string;
  /** Palette slot, so two environments are never confusable at a glance. */
  color: EnvLabelColor;
}

// Amber for dev (unchanged from the original isDev chip), magenta for staging —
// far enough apart in the neon tactical palette to be told apart peripherally.
// Anything else opts into the info blue.
const ENV_LABEL_COLORS: Record<string, EnvLabelColor> = {
  DEV: 'warning',
  STAGING: 'secondary',
};

const DEFAULT_ENV_LABEL_COLOR: EnvLabelColor = 'info';

/**
 * Resolve the environment marker from the raw build-time values.
 *
 * Exported separately from {@link ENV_LABEL} so the precedence rules are testable
 * without rebuilding the bundle.
 *
 * @param rawLabel value of REACT_APP_ENV_LABEL, if any
 * @param nodeEnv  value of NODE_ENV
 * @returns the marker to render, or `null` when the build is unlabelled (production)
 */
export function resolveEnvLabel(
  rawLabel: string | undefined,
  nodeEnv: string | undefined
): EnvLabel | null {
  const explicit = (rawLabel ?? '').trim();
  // An explicit label always wins, including in development — that is how you
  // point a local dev server at staging and have it say so.
  const label = explicit !== '' ? explicit.toUpperCase() : nodeEnv === 'development' ? 'DEV' : '';
  if (label === '') return null;
  return { label, color: ENV_LABEL_COLORS[label] ?? DEFAULT_ENV_LABEL_COLOR };
}

/** The marker for this build, or `null` in production. Resolved once at load. */
export const ENV_LABEL: EnvLabel | null = resolveEnvLabel(
  process.env.REACT_APP_ENV_LABEL,
  process.env.NODE_ENV
);

/**
 * Browser tab title, prefixed on labelled builds so several open tabs stay
 * distinguishable: `[STAGING] CaosBlitz`. Production returns the base title as-is.
 */
export function getDocumentTitle(baseTitle: string, envLabel: EnvLabel | null = ENV_LABEL): string {
  return envLabel ? `[${envLabel.label}] ${baseTitle}` : baseTitle;
}
